import {React, observer, useIntersectionObserver, useLocalStore, useEffect, Fragment} from '@api'
import {CentricImageDefinition} from '@api/graphql'
import {FilterGroup, FilterGroupItems, useFilterStore} from '@modules'
import {FilterGroupRanges} from '@modules/catalog/internal/filters/components/internal/FilterGroupRanges'
import {ICatalogFieldConfig, isNumericAttribute, LibraryItemConfigWrapper, useCatalog} from '@stores'
import {autorun, observable, runInAction, toJS, trace} from 'mobx'

interface IAggregateFilterData {
  doc_count: number,
  count: { value: number },
  items: { doc_count?: number, buckets?: Array<{ doc_count, key, node: { hits: { hits: Array<{ _source: { images: Array<CentricImageDefinition> } }> } } }>, sum_other_doc_count: number },
  unassigned?: { doc_count: number }
}

interface MyProps {
  field: ICatalogFieldConfig,
  maxValuesToLoad?: number,
  className?: string
  index?: number
  library?: LibraryItemConfigWrapper
  expanded?: boolean
  noTitle?: boolean
  isPopover?: boolean
  ignoreSection?: boolean
  ignoreSearch?: boolean
}

export class BoardFilterGroupStore {
  @observable isIntersecting = false
  @observable everVisible = false

  registerEffects = () => {
    const toDispose = [
      autorun(() => {
        this.everVisible = this.everVisible || this.isIntersecting
      })
    ]
    return () => toDispose.forEach(f => f())
  }
}

const BoardFilterGroupStoreContext = React.createContext<BoardFilterGroupStore>(null)
export const useBoardFilterGroupStore = () => React.useContext(BoardFilterGroupStoreContext)

export const BoardFilterGroup = observer<MyProps>(
  function BoardFilterGroup(props) {
  const filter = useFilterStore(),
    [{isIntersecting}, ref] = useIntersectionObserver(),
    store = useLocalStore(() => new BoardFilterGroupStore()),
    catalog = useCatalog()

    useEffect(() => store.registerEffects())
    useEffect(() => {
      store.isIntersecting = props.isPopover || isIntersecting
    }, [isIntersecting, props.isPopover])

  if (!props) return null

  const {field, index, ignoreSection} = props,
    {state: {groups}, everVisible} = filter,
    group = groups[field.field]

    if (group) {
      const {loading, strings} = filter

      return !everVisible
             ? null
             : <BoardFilterGroupStoreContext.Provider value={store}>
               <FilterGroup
          ref={ref}
               className={props.className}
               noTitle={props.noTitle}
               expanded={props.expanded}
               title={catalog.labelForField(field, strings.noValue, 'singular', false)}
               data-index={index}
               group={field.field}
               loading={loading || !isNumericAttribute(field.def) || !group.items}
               ignoreSection={ignoreSection}
             >
               {store.everVisible && (props.expanded || group?.everExpanded) &&
                <Fragment>
                  {isNumericAttribute(field.def) ? <FilterGroupRanges group={field.field}/> :
                   <FilterGroupItems data={group.items as any} group={field.field} ignoreSearch={props.ignoreSearch}/>
                  }
                </Fragment>}
             </FilterGroup>
             </BoardFilterGroupStoreContext.Provider>
    }
    return null
}, )

BoardFilterGroup['defaultProps'] = {maxValuesToLoad: 20}

// endregion


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/filters/components/internal/BoardFilterGroup.tsx